import { Controller, useFormContext, ControllerRenderProps } from "react-hook-form";
import { translate } from "@kaltura/mediaspace-shared-utils";
import { useRef } from "react";
import { StyledDescription, StyledGrid, StyledLabel, StyledTemplateItem, StyledTemplateText } from "./styledComponents";
import { TemplateType, CreateRoomFormValues } from "../types";
import { Typography } from "@kaltura/ds-react-components";
import { InteractiveIllustration, PanelIllustration, CustomIllustration } from "./illustrations";
import { useButtonAnalytics } from "@kaltura/mediaspace-shared-hooks";
import { ButtonClickAnalyticsType } from "@kaltura/mediaspace-shared-types";

interface TemplatesProps {
    templates: TemplateType[];
}

const illustrationsMap: Record<string, React.FC> = {
    InteractiveIllustration,
    PanelIllustration,
};

/**
 * Templates component - provides an accessible grid of room modes (templates) for room creation
 */
const Templates = (props: TemplatesProps) => {
    const { templates } = props;
    const { control } = useFormContext<CreateRoomFormValues>();
    const templateRefs = useRef(new Map<string, HTMLDivElement>());
    const templateIds = templates.map((template) => template.id);

    const sendButtonAnalytics = useButtonAnalytics();

    /**
     * returns the appropriate illustration component based on template data
     */
    const getIllustration = (template: TemplateType) => {
        if (template.illustration) {
            // only non-custom templates contain illustration prop
            const Illustration = template.illustration && illustrationsMap[template.illustration];
            return <Illustration />;
        }
        return <CustomIllustration />;
    };

    const handleTemplateClick = (
        template: TemplateType,
        field: ControllerRenderProps<CreateRoomFormValues, "roomMode">
    ) => {
        sendButtonAnalytics(`Toggle Room template - ${template.name}`, ButtonClickAnalyticsType.TOGGLE);
        field.onChange(template.id);
    };

    const handleTemplateKeyDown = (
        e: React.KeyboardEvent<HTMLDivElement>,
        index: number,
        field: ControllerRenderProps<CreateRoomFormValues, "roomMode">
    ) => {
        let nextIndex: number;
        switch (e.key) {
            case "ArrowRight":
            case "ArrowDown":
                e.preventDefault();
                nextIndex = (index + 1) % templateIds.length;
                break;
            case "ArrowLeft":
            case "ArrowUp":
                e.preventDefault();
                nextIndex = (index - 1 + templateIds.length) % templateIds.length;
                break;
            case "Enter": {
                e.preventDefault();
                // Find the closest form and submit it
                const form = e.currentTarget.closest("form");
                if (form) {
                    form.requestSubmit();
                }
                return;
            }
            default:
                return;
        }
        const nextTemplate = templates[nextIndex];
        templateRefs.current.get(nextTemplate.id)?.focus();
        sendButtonAnalytics(`Toggle Room template - ${nextTemplate.name}`, ButtonClickAnalyticsType.TOGGLE);
        field.onChange(nextTemplate.id);
    };

    return (
        <div>
            <StyledLabel variant={"formLabel"}>
                {templates.length > 1 ? translate("Room mode") : translate("Your room mode")}
            </StyledLabel>{" "}
            <Controller
                name="roomMode"
                control={control}
                defaultValue={templateIds[0] || ""}
                render={({ field }) => (
                    <StyledGrid
                        className="kms-ds-create-room-modal-form-templates"
                        role="radiogroup"
                        aria-label={translate("Room Modes")}
                        singleItem={templates.length === 1}
                    >
                        {templates.map((template: TemplateType, index: number) => (
                            <StyledTemplateItem
                                key={template.id}
                                ref={(element: HTMLDivElement | null) => {
                                    if (element) {
                                        templateRefs.current.set(template.id, element);
                                    }
                                }}
                                isSelected={templates.length > 1 && field.value === template.id}
                                onClick={() => handleTemplateClick(template, field)}
                                onKeyDown={(e) => handleTemplateKeyDown(e, index, field)}
                                role="radio"
                                aria-checked={field.value === template.id}
                                tabIndex={field.value === template.id ? 0 : -1}
                            >
                                {getIllustration(template)}
                                <StyledTemplateText>
                                    <Typography variant={"body2Highlight"}>{template.name}</Typography>
                                    <StyledDescription variant={"body2"}>{template.description}</StyledDescription>
                                </StyledTemplateText>
                            </StyledTemplateItem>
                        ))}
                    </StyledGrid>
                )}
            />
        </div>
    );
};

export default Templates;
